import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import Select from 'react-select';
import { SERVER_BASE } from '../config/server';
import { useTable, useSortBy, usePagination, useGlobalFilter } from 'react-table';
import * as XLSX from 'xlsx';
import 'bootstrap/dist/css/bootstrap.min.css';
import Swal from 'sweetalert2';
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from 'moment';
import DateRangePickerComponent from './CustomDateRangePicker';
import { Link } from 'react-router-dom';

const GlobalItemTransactionReport = ({ accessOfDepartments }) => {
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemList, setItemList] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const code = localStorage.getItem('code');
  const token = localStorage.getItem('token');

  const fetchItems = async () => {
    try {
      const response = await apiClient.get(`${SERVER_BASE}/getItemOptionsByDepartment`, {
        params: {
          code,
          accessOfDepartments
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setItemList(response.data.map((itm) => ({
        value: itm.id,
        label: itm.printName,
        uom: itm.uom
      })));
    } catch (error) {
      console.error("Error fetching items:", error);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [accessOfDepartments]);

  const handleGetData = async () => {
    const [startDate, endDate] = dateRange;

    if (!startDate || !endDate) {
      Swal.fire({
        icon: 'warning',
        title: 'Oops...',
        text: 'Please select a valid date range!',
        position: 'top-end',
        showConfirmButton: false,
        timer: 1500,
        toast: true,
      });
      return;
    }

    if (!selectedItem) {
      Swal.fire({
        icon: 'warning',
        title: 'Oops...',
        text: 'Please select inventory name!',
        position: 'top-end',
        showConfirmButton: false,
        timer: 1500,
        toast: true,
      });
      setReportData([]);
      return;
    }

    setLoading(true);


    try {
      const response = await apiClient.get(`${SERVER_BASE}/getItemInventoryReport`, {
        params: {
          itemId: selectedItem.value,
          startDate: moment(startDate).format('YYYY-MM-DD'),
          endDate: moment(endDate).format('YYYY-MM-DD'),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setReportData(response.data); // { openingRow, inventoryRows }
    } catch (error) {
      console.error("Error fetching item inventory report:", error);

    } finally {
      setLoading(false);
    }
  };
  const handleExportExcel = async () => {
    if (!processedItems.length) {
      Swal.fire({
        icon: "warning",
        title: "No data to export",
        toast: true,
        position: "top-end",
        timer: 2000,
        showConfirmButton: false,
      });
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Item Ledger");

    // ================= HEADER =================
    worksheet.columns = [
      { header: "S.No", key: "sno", width: 8 },
      { header: "Date", key: "date", width: 15 },
      { header: "Department", key: "department", width: 20 },
      { header: "Location", key: "location", width: 20 },
      { header: "Particulars", key: "particulars", width: 30 },
      { header: "Voucher", key: "voucher", width: 20 },
      { header: "Ledger", key: "ledger", width: 25 },
      { header: "Additions", key: "additions", width: 15 },
      { header: "Deductions", key: "deductions", width: 15 },
      { header: "Closing Qty", key: "closing", width: 15 },
      { header: "UOM", key: "uom", width: 10 },
    ];

    worksheet.getRow(1).font = { bold: true };

    // ================= DATA =================
    processedItems.forEach((row, index) => {
      worksheet.addRow({
        sno: index + 1,
        date: row.affectedDate
          ? moment(row.affectedDate).format("DD-MMM-YYYY")
          : "",
        department: row.department || "",
        location: row.location || "",
        particulars: row.particulars || row.remarks || "",
        voucher: row.transactionCode || "",
        ledger: row.ledgerName || "",
        additions: row.type === "add" ? Number(row.quantity) : "",
        deductions: row.type === "less" ? Number(row.quantity) : "",
        closing: Number(row.runningQuantity).toFixed(2),
        uom: row.uom || "",
      });
    });

    // ================= OPENING / CLOSING BOLD =================
    worksheet.eachRow((row) => {
      const particulars = row.getCell("E").value;
      if (particulars === "Opening" || particulars === "Closing") {
        row.font = { bold: true };
      }
    });

    // ================= DOWNLOAD =================
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(
      blob,
      `Item_Ledger_${selectedItem?.label || "Item"}_${moment().format(
        "DDMMYYYY_HHmm"
      )}.xlsx`
    );
  };

  const processedItems = React.useMemo(() => {
    if (!reportData || !reportData?.inventoryRows || !reportData?.openingRow) return [];

    const { openingRow, inventoryRows } = reportData;
    let runningQty = openingRow.openingQty;

    const openingEntry = {
      affectedDate: moment(dateRange[0]).format("YYYY-MM-DD"),
      department: '',
      location: '',
      particulars: 'Opening',
      transactionCode: '',
      quantity: openingRow.openingQty,
      type: '', // assumed
      runningQuantity: runningQty,
      uom: selectedItem?.uom,
      ledgerName: "-"
    };

    const dataRows = inventoryRows.map((row) => {
      const qty = parseFloat(row.quantity) || 0;
      runningQty += row.type === 'add' ? qty : -qty;

      return {
        ...row,
        runningQuantity: runningQty,
        uom: selectedItem?.uom,
      };
    });

    const closingEntry = {
      affectedDate: moment(dateRange[1]).format("YYYY-MM-DD"),
      department: '',
      location: '',
      particulars: 'Closing',
      transactionCode: '',
      quantity: '',
      type: '',
      runningQuantity: runningQty,
      uom: selectedItem?.uom,
      ledgerName: "-"
    };

    return [openingEntry, ...dataRows, closingEntry];
  }, [reportData, dateRange]);

  const columns = React.useMemo(() => [
    {
      Header: "S.No",
      Cell: ({ row }) => row.index + 1,
    },
    {
      Header: 'Date',
      accessor: 'affectedDate',
      Cell: ({ value }) => moment(value).format("DD-MMM-YYYY"),
    },
    {
      Header: 'Department',
      accessor: 'department',
    },
    {
      Header: 'Location',
      accessor: 'location',
    },
    {
      Header: 'Particulars',
      accessor: 'particulars',
      Cell: ({ row }) => row.original.particulars || row.original.remarks || '-',
    },
    {
      Header: 'Voucher',
      accessor: 'transactionCode',
      Cell: ({ row }) => {
        const { transactionCode, voucherType, voucherId } = row.original;

        if (!voucherType || !voucherId) {
          return transactionCode || '-';
        }

        let path = "";

        switch (voucherType) {
          case "Receipt Voucher":
            path = `/PreviewExternalReceiptVoucher?voucherId=${voucherId}&type=external`;
            break;

          case "Delivery Challan":
            path = `/previewDeliveryChallan?challanId=${voucherId}&type=challan`;
            break;

          case "Addition":
            path = `/previewInventory?draftId=${voucherId}&type=inventory`;
            break;

          case "Deduction":
            path = `/previewInventory?draftId=${voucherId}&type=inventory`;
            break;

          case "Transaction Entry":
            path = `/TransactionEntryPreview?draftId=${voucherId}&type=transactionEntry`;
            break;

          case "Unusable Production":
            path = `/UnusableProductionPreview?draftId=${voucherId}&type=unusable`;
            break;

          case "Conversion":
            path = `/ItemConversionPreview?conversionId=${voucherId}&type=conversion`;
            break;
            case "Issue Slip":
              path = `/IssueSlipPreview?draftId=${voucherId}&type=issueRequest`;
              break;
          default:
            return transactionCode || '-';
        }

        return (
          <Link
            to={path}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline" }}
          >
            {transactionCode}
          </Link>
        );
      },
    },

    {
      Header: 'Ledger',
      accessor: 'ledgerName',
    },
    {
      Header: 'Additions',
      accessor: 'additions',
      Cell: ({ row }) => {
        const { type, quantity } = row.original;
        if (type === 'add') {
          const val = Number(quantity);
          return val % 1 === 0 ? val : val.toFixed(2);
        }
        return '';
      },
    },
    {
      Header: 'Deductions',
      accessor: 'deductions',
      Cell: ({ row }) => {
        const { type, quantity } = row.original;
        if (type === 'less') {
          const val = Number(quantity);
          return val % 1 === 0 ? val : val.toFixed(2);
        }
        return '';
      },
    },

    {
      Header: 'Closing',
      accessor: 'runningQuantity',
      Cell: ({ value }) => {
        if (value == null) return '';
        return Number(value) % 1 === 0 ? Number(value) : Number(value).toFixed(2);
      }
    },
    {
      Header: 'UOM',
      accessor: 'uom',
    },
  ], []);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { pageIndex, pageSize, globalFilter },
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data: processedItems,
      initialState: { pageIndex: 0 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );


  return (
    <div className="formcontainer mt-3">
      <h5>Item Ledger
      </h5>
      <div className="row align-items-end mb-4">
        <div className="col-md-4">
          <label>Select Inventory Name:</label>
          <Select
            options={itemList}
            value={selectedItem}
            onChange={setSelectedItem}
            isClearable
          />
        </div>
        <div className="col-md-3">
          <label>Date Range:</label>
          {/* <Stack direction="column" spacing={8} alignItems="flex-start"> */}
          <DateRangePickerComponent value={dateRange} onChange={setDateRange} />
          {/* </Stack> */}
        </div>
        <div className="col-md-4 d-flex align-items-center">
          <button
            onClick={handleGetData}
            disabled={loading}
            className="btn btn-outline-success me-2"
          >
            {loading ? 'Loading...' : 'Get Report'}
          </button>
          <button
            onClick={handleExportExcel}
            className="btn btn-outline-primary"
            disabled={!processedItems.length}
          >
            Export Excel
          </button>
        </div>

      </div>
      <div className="col-md-12 mb-4">
        <input
          type="text"
          className="form-control"
          placeholder="Search store / area"
          value={globalFilter || ''}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />
      </div>
      <div className="table-responsive">
        <table
          style={{ fontSize: '15px', verticalAlign: 'baseline' }}
          className="table table-striped table-bordered"
          {...getTableProps()}
        >
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render('Header')}
                    <span>
                      {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row, i) => {
              prepareRow(row);
              const isOpening = row.original.particulars === 'Opening';
              const isClosing = row.original.particulars === 'Closing';
              return (
                <tr {...row.getRowProps()} style={{ fontWeight: isOpening || isClosing ? 'bold' : 'normal' }}>
                  {row.cells.map(cell => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="d-flex justify-content-between align-items-center mt-3">

          <button
            className="btn btn-secondary me-2"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            Previous
          </button>
          <span>
            Page {pageIndex + 1} of {pageOptions.length}
          </span>
          <button
            className="btn btn-secondary"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default GlobalItemTransactionReport;
